import { EventPairing, EventResult, EventResultClaim, EventResultFieldDef, EventResultOutcome } from './models';

/** One result field's figures within a single player's account of the game. */
export interface ClaimFieldSummary {
    label: string;
    /** Participant A's figure then B's, in pairing order -- "12–8", with "-" for a missing figure. */
    text: string;
    /** True when the OTHER account reports different figures for this same field. */
    differs: boolean;
}

/**
 * One player's whole account of a game, reduced to a single scannable line.
 *
 * Deliberately carries the outcome rather than a rendered "X won" string: the caller
 * owns the wording, so the organiser's modal can reuse the translation keys it already
 * uses elsewhere instead of this file hardcoding English.
 */
export interface ClaimSummary {
    /** The player whose account this is. */
    reporterName: string;
    outcome: EventResultOutcome;
    /** The winner's display name, or null for a draw. */
    winnerName: string | null;
    /** True when the two accounts disagree about who won. */
    outcomeDiffers: boolean;
    fields: ClaimFieldSummary[];
    /** The underlying claim, so the caller can adopt it wholesale. */
    claim: EventResultClaim;
}

function participantName(participant: { displayName?: string; userId?: string } | undefined, fallback: string): string {
    return participant?.displayName || participant?.userId || fallback;
}

function figure(values: Record<string, number> | undefined, key: string): number | null {
    const value = values?.[key];
    return value == null ? null : value;
}

/**
 * Both players' accounts of a disputed game, side by side, with the figures they disagree
 * about marked.
 *
 * Returns an EMPTY array unless there are genuinely two differing accounts to show, so a
 * caller can treat a non-empty result as "there is a disagreement here" and needs no
 * separate status check. That covers three cases: a result with only one report (nothing
 * to compare), a legacy result predating stored claims (same), and two accounts that
 * agree -- which is a confirmed result, not a dispute, and so has nothing to resolve.
 *
 * Order is always participant A then participant B, matching the override form's own
 * column order, rather than who reported most recently.
 */
export function compareResultClaims(
    result: EventResult | undefined,
    pairing: EventPairing | undefined,
    resultFields: EventResultFieldDef[] = []
): ClaimSummary[] {
    const claimA = result?.participantAClaim;
    const claimB = result?.participantBClaim;
    if (!result || !pairing || !claimA || !claimB) {
        return [];
    }

    const nameA = participantName(pairing.participantA, 'A');
    const nameB = participantName(pairing.participantB, 'B');
    const winnerName = (outcome: EventResultOutcome) => (outcome === 'DRAW' ? null : outcome === 'A_WIN' ? nameA : nameB);

    const outcomeDiffers = claimA.outcome !== claimB.outcome;
    let anyFieldDiffers = false;

    const fieldsFor = (mine: EventResultClaim, theirs: EventResultClaim): ClaimFieldSummary[] =>
        resultFields.map((field) => {
            const key = String(field.id);
            const myA = figure(mine.participantAValues, key);
            const myB = figure(mine.participantBValues, key);
            const differs = myA !== figure(theirs.participantAValues, key) || myB !== figure(theirs.participantBValues, key);
            if (differs) {
                anyFieldDiffers = true;
            }
            // En dash separator, hyphen for a missing figure -- same formatting as
            // ResultSummaryPipe, so a claim line and a result line read alike.
            return { label: field.label, text: `${myA ?? '-'}–${myB ?? '-'}`, differs };
        });

    const summaries: ClaimSummary[] = [
        {
            reporterName: nameA,
            outcome: claimA.outcome,
            winnerName: winnerName(claimA.outcome),
            outcomeDiffers,
            fields: fieldsFor(claimA, claimB),
            claim: claimA
        },
        {
            reporterName: nameB,
            outcome: claimB.outcome,
            winnerName: winnerName(claimB.outcome),
            outcomeDiffers,
            fields: fieldsFor(claimB, claimA),
            claim: claimB
        }
    ];

    // Built before this check because working out whether any field differs IS the
    // per-field comparison above; there is nothing cheaper to test first.
    return outcomeDiffers || anyFieldDiffers ? summaries : [];
}
